import React, { useState } from "react";
import "./ProductTips.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAngleLeft, faAngleRight } from "@fortawesome/pro-solid-svg-icons";


const tips = [
    () => (
        <>
            <h2 className="title">
                Find project configuration issues before your participants do
            </h2>
            <p>Run Project Review to check your project against a set of rules built for the kind of logic errors and gaps that are easy to miss on a manual read-through.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/project-review.png" alt="Project review"></img>
            <p>Project Review checks for:</p>
            <ul>
                <li>Branching logic that has an unreachable question.</li>
                <li>References to custom fields that are not defined.</li>
                <li>SMS notifications that can land in the middle of the night.</li>
                <li>Projects missing a support email participants can reach.</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/testing-your-project" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Get phone-framed screenshots of your survey steps
            </h2>
            <p>Export screenshots of every step in your survey, rendered inside a device frame, ready for a review or IRB submission.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/mdh_png_export.png" alt="Survey screenshot export"></img>
            <p>Use it to:</p>
            <ul>
                <li>See how your survey looks on a specific device, like an iPhone 16 or Samsung Galaxy S24.</li>
                <li>Export screenshots of all of your survey steps.</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/creating-a-survey" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Quickly make broad updates to your survey
            </h2>
            <p>The Survey Copilot takes a plain-language description of what you want changed and applies it directly to your survey — new steps, branching logic, calculated fields, language tweaks, restructuring — so you're reviewing a finished edit instead of updating it manually.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/survey-copilot2.png" alt="Survey copilot"></img>
            <p>Try asking copilot:</p>
            <ul>
                <li>“Add a step that collects height and weight and calculates BMI”</li>
                <li>“Turn this form item step into individual question steps”</li>
                <li>“Add a calculated field for a total score of the first 5 survey steps”</li>
                <li>“Review for best practices and make suggested improvements”</li>
            </ul>
            <p>Tip: Attach a file, describe the change in plain language, or pick from a list of preset prompts.</p>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/creating-a-survey" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Turn your protocol and survey documents into a working study in minutes
            </h2>
            <p>Project Quickstart reads your study documents and drafts your surveys, consent forms, and basic project configuration — so you start from a working foundation, not a blank page.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/project-quickstart.png" alt="Project quickstart"></img>
            <p>Use it to:</p>
            <ul>
                <li>Upload a study protocol and get drafted surveys built around it — if it references a validated instrument like PHQ-9, GAD-7, or PROMIS, Designer recognizes it and builds it to spec.</li>
                <li>Bring your consent forms, data dictionary, or instrument PDFs and Designer configures them directly rather than you entering them by hand.</li>
                <li>Migrate from REDCap or Qualtrics by uploading your .xml or .qsf export — your existing surveys carry over.</li>
                <li>Let Designer handle the project basics for you — sensor data and EHR settings, enrollment settings, and support information, set up and ready to refine.</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/creating-and-renaming-a-project" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Generate new surveys from documents, exports, or a plain description
            </h2>
            <p>Survey Generator builds editable surveys with navigation logic and calculated fields already included from a description, a paste, or a document you upload.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/survey-generator.png" alt="Survey generator"></img>
            <p>Generate surveys by:</p>
            <ul>
                <li>Pasting in existing survey instruments, documents, or consent forms.</li>
                <li>Describing a measure or what you're trying to capture in plain language.</li>
                <li>Uploading a document (.pdf, .docx, .csv, .ppt, .xml) or a Qualtrics/REDCap export.</li>
                <li>Specifying your survey scoring or calculated field logic — like a PHQ-9 total score or a BMI calculation — to build it in alongside the questions.</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/creating-a-survey" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Confidently promote tested updates to your live project
            </h2>
            <p>Using Project Compare, copy over updates you made and tested in a Dry Run project to a live project.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/project-compare.png" alt="Project compare"></img>
            <p>Use it to:</p>
            <ul>
                <li>Spot configuration differences between projects at a glance</li>
                <li>Copy surveys, segments, schedules, and more from Dry Run to Production</li>
                <li>Keep your live project aligned with tested changes</li>
                <li>Confidently promote updates without manual work</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/project-compare-selectively-sync-project-configurations" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Pin segments to keep track of critical participant groups
            </h2>
            <p>Once a segment is featured, it's visible to all project users, keeping your team aligned & focused. Just 
                click the gear icon above the Participants list to select the segments you want to feature.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/featured-segments.png" alt="Featured segments"></img>
            <p>Study teams often use Featured Segments to:</p>
            <ul>
                <li>Quickly view participants who haven't completed enrollment</li>
                <li>Monitor key milestones and study progress of participants</li>
                <li>Focus on high-priority groups needing outreach or intervention</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/segments-best-practices-examples#featured-segments" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Create personalized participant journeys
            </h2>
            <p>App Designer enables you to customize the participant journey, not only for your project, but for 
                each individual participant.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/app-designer.png" alt="App designer"></img>
            <p>Using conditional sections and other basic App Designer components, you can:</p>
            <ul>
                <li>Direct participants to their next best action—whether that's completing a survey, connecting 
                    to an EHR or wearable device, or placing an order for a study device or drug.</li>
                <li>Surface personalized, engaging content, specific to a participant—if your participant 
                    indicates they have not been sleeping well, they could be shown an article on developing 
                    better sleep habits.</li>
            </ul>
            <p>Tip: Thoroughly test your conditional logic to ensure a smooth participant experience.</p>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/app-designer" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Automatically send participants their signed consent form
            </h2>
            <p>When setting up your consent or consent form step, choose an email notification and the participant will receive their signed consent form as a PDF attachment.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/send-signed-consent-form.png" alt="Send signed consent form"></img>
            <p>Here's how:</p>
            <ul>
                <li>Create an email notification (for example, using the Informational V2 email template)</li>
                <li>Link the notification to your consent or consent form step</li>
                <li>Publish both the updated survey and the new notification</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/step-type-consent" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Enhance Your Surveys with Images and Rich Formatting
            </h2>
            <p>Use the rich text editor and GitHub Flavored Markdown to easily add custom formatting, visuals, 
                and structured content to your survey steps.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/enhance-surveys.png" alt="Enhance surveys"></img>
            <p>Use it to:</p>
            <ul>
                <li>Insert images directly into your survey content where relevant</li>
                <li>Add tables, headings, code blocks, multi-level lists, and other formatting elements</li>
                <li>Copy and paste pre-formatted content while preserving its layout and style</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/formatting-survey-text" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Monitor participant compliance using Segments
            </h2>
            <p>Monitor who is on track, who needs follow-up, and when to intervene—then trigger automated actions 
                when they meet specific criteria.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/monitoring-compliance-using-segments.png" alt="Monitoring compliance using segments"></img>
            <p>Use it to:</p>
            <ul>
                <li>Flag participants who haven't completed recent surveys or tasks</li>
                <li>Monitor wearable device sync and reconnection needs</li>
                <li>Track study milestone completion (Baseline, clinic visits, follow-ups, etc)</li>
            </ul>
            <p>Once you set up a segment, automate your outreach using an On Entering a Segment schedule.</p>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/filtering-with-participant-segments" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Capture participant touchpoints and status updates using Coordinator Surveys
            </h2>
            <p>Coordinator Surveys let you log contact notes, visits, participant status, and other additional 
                information directly within each participant's record. Use captured information to power 
                automated workflows or update segments across your project.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/coordinator-surveys.png" alt="Coordinator surveys"></img>
            <p>Build surveys that coordinators can complete to do the following:</p>
            <ul>
                <li>Log calls, notes, clinic visits, and surgery dates</li>
                <li>Contact participants and logging information</li>
                <li>Document withdrawal reason</li>
                <li>Update participant status</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/using-coordinator-entered-surveys" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Generate calculated fields directly within your surveys.
            </h2>
            <p>Use Calculated Fields to calculate scores, manipulate answers, and utilize advanced logic in real time within a survey—using simple spreadsheet-style expressions based on participant responses and existing fields.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/product-tips-calculated-fields.png" alt="Calculated fields"></img>
            <p>You can generate:</p>
            <ul>
                <li>Scores for assessments like PHQ-9 or GAD-7 as participants complete the survey.</li>
                <li>Metrics like BMI using responses collected in the same survey.</li>
                <li>Randomized values for participant assignment.</li>
                <li>Calculated future follow-up dates based on today's date.</li>
                <li>Aggregate values from responses (e.g., averages, sums, minimums, or maximums of symptom severity scores).</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/calculated-fields" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Track survey statuses directly from your segments.
            </h2>
            <p>Add survey status columns to your segments views to see what surveys have been completed, are incomplete, overdue, or have been closed—even across events—all in one place, without opening individual records.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/product-tips-event-status-columns.png" alt="Event status columns"></img>
            <p>Use it to:</p>
            <ul>
                <li>Identify participants who need follow-up.</li>
                <li>Monitor adherence across study milestones.</li>
                <li>Spot overdue or incomplete tasks at a glance.</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/tracking-survey-completion-with-events" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),
    () => (
        <>
            <h2 className="title">
                Safely pause your project without changing your setup.
            </h2>
            <p>Pause a project when it's complete. Pausing temporarily stops all activity and removes the project from participant access until resumed—without affecting your configuration.</p>
            <img src="https://assets.careevolutionapps.com/CareEvolution/Images/MDHD/product-tips/product-tips-project-pause.png" alt="Project pause"></img>
            <p>Project Pause will:</p>
            <ul>
                <li>Prevent new participant enrollment.</li>
                <li>Pause survey and notification delivery.</li>
                <li>Pause survey and sensor data collection.</li>
                <li>Stop exports from uploading.</li>
                <li>Remove the project from participant accounts.</li>
            </ul>
            <a className="editor-controls-button tertiary-button" href="https://support.mydatahelps.org/project-pausing" target="_blank" rel="noreferrer">Learn More</a>
        </>
    ),

];

const ProductTips: React.FC = () => {
    const [currentTipIndex, setCurrentTipIndex] = useState(() => Math.floor(Math.random() * tips.length));

    const handleNext = () => {
        setCurrentTipIndex((prevIndex) => (prevIndex + 1) % tips.length);
    };

    const handlePrev = () => {
        setCurrentTipIndex((prevIndex) => (prevIndex - 1 + tips.length) % tips.length);
    };

    return (
        <>
            <div className="product-tips">
            <header>
                <button onClick={handlePrev} title="previous">
                    <FontAwesomeIcon icon={faAngleLeft} />
                </button>
                <h3>Discover what's possible</h3>
                <button onClick={handleNext} title="next">
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </header>
            <div className="product-tip">{tips[currentTipIndex]()}</div>
        </div>
        </>
    );
};

export default ProductTips;
